import React from 'react';
import {
  Accordion,
  AccordionButton,
  AccordionIcon,
  AccordionItem,
  AccordionPanel,
  Badge,
  Box,
  Drawer,
  DrawerBody,
  DrawerCloseButton,
  DrawerContent,
  DrawerHeader,
  DrawerOverlay,
  Flex,
  ListItem,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  UnorderedList,
} from '@chakra-ui/react';

/**
 * Manual do motor do Simples para a contabilidade: de onde vem cada dado,
 * como a receita é segregada, como sai o DAS, o que a auditoria confere e
 * como chega na Receita. Texto fixo: manter junto com o backend
 * (services/simples_nacional).
 */

const NIVEL: Record<string, { rotulo: string; cor: string }> = {
  bloqueio: { rotulo: 'Bloqueio', cor: 'red' },
  atencao: { rotulo: 'Atenção', cor: 'orange' },
  info: { rotulo: 'Info', cor: 'gray' },
};

const AUDITORIAS: { grupo: string; itens: [string, string, string][] }[] = [
  {
    grupo: 'Notas e numeração',
    itens: [
      [
        'Quebra de sequência',
        'atencao',
        'Falta número de NF-e/NFC-e (por série) ou de NFS-e no mês: nota cancelada, inutilizada ou não importada. Lista os números que faltam.',
      ],
      [
        'Nota de filial fora do cadastro',
        'atencao',
        'Nota emitida por um CNPJ da raiz que não está nas filiais do cadastro do Simples.',
      ],
      ['Empresa sem CNPJ', 'bloqueio', 'Sem CNPJ não há como declarar.'],
    ],
  },
  {
    grupo: 'Produtos (NF-e / NFC-e, item a item)',
    itens: [
      [
        'CFOP inválido ou de outras saídas',
        'atencao',
        'CFOP que não é venda (remessa, brinde, transferência…) fica fora da receita, mas aparece para conferir.',
      ],
      [
        'CFOP de exportação não mapeado',
        'atencao',
        'Saída para o exterior com CFOP fora da tabela: conferir se é exportação.',
      ],
      [
        'ST: CFOP x CSOSN',
        'atencao',
        'CFOP de ICMS já retido por ST com CSOSN que não é 500: o ICMS ficou dentro do DAS, conferir a nota.',
      ],
      [
        'NCM monofásico para revisar',
        'atencao',
        'NCM perto da lista de monofásicos: PIS/Cofins só saem do DAS na revenda.',
      ],
      ['Monofásico de fabricante', 'info', 'Quem fabrica recolhe PIS/Cofins no DAS normalmente.'],
      ['Industrialização por encomenda', 'info', 'Receita de industrialização para terceiros.'],
      [
        'Serviço em NF-e',
        'atencao',
        'Item de serviço dentro de NF-e conjugada: vai para o anexo de serviço.',
      ],
      [
        'Devoluções',
        'atencao',
        'Devolução de venda abate a receita do mês (Anexo I/II); se passar da receita, o resto vira aviso. A devolução emitida pelo cliente também conta.',
      ],
    ],
  },
  {
    grupo: 'Serviços (NFS-e)',
    itens: [
      [
        'Serviço não mapeado ou ambíguo',
        'atencao',
        'Item da LC 116 sem anexo certo (ex.: pode ser III ou IV): fica no padrão e pede conferência ou regra no cadastro.',
      ],
      [
        'Serviço pelo CNAE / por grupo',
        'info',
        'Anexo decidido pelo CNAE ou pelo grupo do item, não pela regra da empresa.',
      ],
      [
        'Cessão de mão de obra',
        'bloqueio',
        'Cessão ou fornecimento de mão de obra é vedado no Simples, salvo as atividades do Anexo IV (LC 123 art. 17, XII e §5º-H): conferir antes de declarar.',
      ],
      [
        'ISS de outro município sem código TOM',
        'bloqueio',
        'O PGDAS-D pede o código TOM (Tesouro) do município do ISS; sem ele não declara.',
      ],
    ],
  },
  {
    grupo: 'RBT12 e Fator R',
    itens: [
      [
        'RBT12 sem fonte confirmada',
        'bloqueio',
        'Sem histórico e sem a Receita. Resolve com "Importar da Receita", o rascunho no SERPRO ou lançando as receitas dos 12 meses.',
      ],
      [
        'RBT12 nosso x Receita',
        'atencao',
        'Nossas declarações somam diferente do que a Receita tem: vale o da Receita (houve declaração por fora?).',
      ],
      ['RBT12 acima de R$ 4,8 mi', 'bloqueio', 'Passou do limite do Simples: exclusão.'],
      [
        'Sublimite de R$ 3,6 mi',
        'atencao',
        'Na 6ª faixa ICMS e ISS saem do DAS e são pagos por fora.',
      ],
      [
        'Fator R sem folha / perto de 28%',
        'atencao',
        'Sem folha o serviço do Fator R fica no Anexo V; perto de 28% um lançamento de folha muda o anexo.',
      ],
      [
        'Folha obrigatória',
        'bloqueio',
        'Atividade do Fator R exige a folha dos 12 meses na declaração.',
      ],
    ],
  },
  {
    grupo: 'Comparações e mês',
    itens: [
      [
        'Diferença com o PGDAS-D (rascunho)',
        'bloqueio',
        '1 centavo de diferença em qualquer tributo e a Receita recusa a transmissão.',
      ],
      [
        'Diferença com o Domínio',
        'atencao',
        'Acima de R$ 1. O Domínio é só referência, nunca entra no cálculo.',
      ],
      [
        'Variação contra o mês anterior',
        'atencao',
        'DAS mais de 30% acima ou abaixo do mês anterior.',
      ],
      ['Mês anterior não transmitido', 'atencao', 'O mês anterior ainda não foi declarado.'],
      [
        'Sem movimento',
        'info',
        'Sem receita: declara zerado (sem DAS), mas baixa declaração e recibo.',
      ],
      [
        'Correção manual',
        'info',
        'Alguém trocou a segregação de um documento: fica registrado quem e por quê.',
      ],
      [
        'Aprovação desfeita',
        'atencao',
        'Notas, correções ou RBT12 mudaram depois de aprovar: confere e aprova de novo.',
      ],
      [
        'Retificação necessária',
        'atencao',
        'Depois de transmitido, o cálculo mudou: avaliar retificadora.',
      ],
      [
        'Sem procuração / declaração já existe',
        'bloqueio',
        'Vem do SERPRO: falta procuração no e-CAC, ou o mês já foi declarado por fora.',
      ],
    ],
  },
];

function Secao({ titulo, children }: { titulo: string; children: React.ReactNode }) {
  return (
    <AccordionItem border="1px solid #E2E8F0" borderRadius="12px" mb={2} bg="white">
      <AccordionButton py={3} borderRadius="12px">
        <Text flex={1} textAlign="left" fontWeight={700} fontSize="14px" color="#0F172A">
          {titulo}
        </Text>
        <AccordionIcon />
      </AccordionButton>
      <AccordionPanel pt={0} pb={4} fontSize="13px" color="#334155" lineHeight="1.6">
        {children}
      </AccordionPanel>
    </AccordionItem>
  );
}

function Passo({ n, titulo, children }: { n: number; titulo: string; children: React.ReactNode }) {
  return (
    <Flex gap={3} mb={3}>
      <Flex
        w="26px"
        h="26px"
        flex="none"
        borderRadius="full"
        bg="#1B88F5"
        color="white"
        fontSize="12px"
        fontWeight={700}
        align="center"
        justify="center"
      >
        {n}
      </Flex>
      <Box>
        <Text fontWeight={700} color="#0F172A">
          {titulo}
        </Text>
        <Box color="#475569">{children}</Box>
      </Box>
    </Flex>
  );
}

export function MotorCalculoDrawer({
  aberto,
  onFechar,
}: {
  aberto: boolean;
  onFechar: () => void;
}) {
  return (
    <Drawer isOpen={aberto} onClose={onFechar} size="lg" placement="right">
      <DrawerOverlay />
      <DrawerContent>
        <DrawerCloseButton />
        <DrawerHeader borderBottom="1px solid #E2E8F0">
          <Text fontSize="17px" fontWeight={700}>
            Motor de cálculo do Simples Nacional
          </Text>
          <Text fontSize="12px" color="#64748B" fontWeight={400}>
            De onde vem cada número, o que é conferido e como chega na Receita.
          </Text>
        </DrawerHeader>
        <DrawerBody bg="#F8FAFC" pt={4}>
          <Box mb={4} p={3} borderRadius="12px" bg="#EFF6FF" fontSize="13px" color="#1E3A8A">
            O cálculo é feito aqui, nota a nota, pelas regras da LC 123/2006. O Domínio entra só
            como gabarito para comparar e <b>nunca</b> é usado no cálculo.
          </Box>

          <Accordion allowMultiple defaultIndex={[0]}>
            <Secao titulo="O caminho, do começo ao fim">
              <Passo n={1} titulo="Carteira do mês">
                Entram as empresas do Simples, ativas e com o mês dentro do período de cliente (do
                mês do &quot;cliente desde&quot; até o do &quot;cliente até&quot;).
              </Passo>
              <Passo n={2} titulo="Importação">
                Notas, faturas sem nota, folha e o histórico de receita do mês (ver &quot;De onde
                vêm os dados&quot;).
              </Passo>
              <Passo n={3} titulo="Segregação">
                Cada item vira uma linha de receita com anexo e tributos fora do DAS (ST,
                monofásico, ISS retido, exportação).
              </Passo>
              <Passo n={4} titulo="Cálculo">
                RBT12 → faixa → alíquota efetiva → valor de cada tributo por anexo → DAS.
              </Passo>
              <Passo n={5} titulo="Auditoria">
                Gera as pendências. Bloqueio impede aprovar; atenção precisa de aceite
                (justificativa opcional); info só informa.
              </Passo>
              <Passo n={6} titulo="Rascunho na Receita (opcional)">
                Manda a declaração <b>sem transmitir</b>: a Receita devolve o cálculo dela, e 1
                centavo de diferença vira bloqueio.
              </Passo>
              <Passo n={7} titulo="Aprovação">
                Responsável fiscal, gestão, diretoria ou admin. Congela o que foi aprovado: se algo
                mudar, a aprovação cai sozinha.
              </Passo>
              <Passo n={8} titulo="Transmissão">
                Só a original. Antes consulta a Receita: mês já declarado não é enviado. Depois gera
                DAS e extrato e guarda os PDFs.
              </Passo>
              <Passo n={9} titulo="Retificação">
                Ação separada, na empresa, com o CNPJ digitado como confirmação. O DAS novo é gerado
                à parte, porque a guia original pode já ter sido paga.
              </Passo>
            </Secao>

            <Secao titulo="De onde vêm os dados">
              <Table size="sm" variant="simple">
                <Thead>
                  <Tr>
                    <Th>Dado</Th>
                    <Th>Fonte</Th>
                  </Tr>
                </Thead>
                <Tbody>
                  <Tr>
                    <Td>Vendas de produto</Td>
                    <Td>
                      XML das NF-e e NFC-e emitidas pela empresa e filiais (item a item; nota
                      repetida conta uma vez)
                    </Td>
                  </Tr>
                  <Tr>
                    <Td>Serviços</Td>
                    <Td>NFS-e da prefeitura (item da LC 116, ISS retido, município, tomador)</Td>
                  </Tr>
                  <Tr>
                    <Td>Devoluções</Td>
                    <Td>NF-e de devolução própria e as emitidas pelo cliente para a empresa</Td>
                  </Tr>
                  <Tr>
                    <Td>Receita sem nota</Td>
                    <Td>Tela de Faturas</Td>
                  </Tr>
                  <Tr>
                    <Td>Folha (Fator R)</Td>
                    <Td>
                      Folha do Domínio (proventos + FGTS + CPP patronal) ou a folha lançada à mão,
                      que vence
                    </Td>
                  </Tr>
                  <Tr>
                    <Td>Receita dos 12 meses (RBT12)</Td>
                    <Td>Declarações transmitidas, importadas da Receita, ou lançadas à mão</Td>
                  </Tr>
                  <Tr>
                    <Td>Anexo do serviço, ISS fixo, ICMS fora</Td>
                    <Td>Aba &quot;Simples Nacional&quot; do cadastro da empresa</Td>
                  </Tr>
                  <Tr>
                    <Td>Município do ISS</Td>
                    <Td>Código IBGE da nota convertido para o código TOM do Tesouro</Td>
                  </Tr>
                  <Tr>
                    <Td>DAS do Domínio</Td>
                    <Td>Só comparação (gabarito)</Td>
                  </Tr>
                </Tbody>
              </Table>
            </Secao>

            <Secao titulo="Como a receita é segregada">
              <UnorderedList spacing={1}>
                <ListItem>
                  <b>CFOP</b> decide se é receita e de que tipo: revenda (Anexo I), produção própria
                  (Anexo II), exportação, serviço ou devolução.
                </ListItem>
                <ListItem>
                  <b>CSOSN 500</b> (ou CFOP x405) = ICMS já retido por ST: o ICMS sai do DAS. O
                  substituto (CSOSN 201/202/203) continua pagando o ICMS próprio no DAS.
                </ListItem>
                <ListItem>
                  <b>NCM monofásico</b>: PIS e Cofins saem do DAS, só na revenda.
                </ListItem>
                <ListItem>
                  <b>Exportação</b>: PIS, Cofins, ICMS, ISS e IPI saem do DAS.
                </ListItem>
                <ListItem>
                  <b>Serviço</b>: o item da LC 116 decide o anexo (III, IV ou V); o cadastro da
                  empresa pode fixar o anexo por atividade.
                </ListItem>
                <ListItem>
                  <b>ISS retido</b> pelo tomador sai do DAS; ISS devido a outro município é
                  declarado com o código TOM dele.
                </ListItem>
                <ListItem>
                  Quando a classificação cai num padrão, sempre vira pendência para conferir. Quem
                  confere pode corrigir a segregação documento a documento (fica registrado).
                </ListItem>
              </UnorderedList>
            </Secao>

            <Secao titulo="Como o DAS é calculado">
              <UnorderedList spacing={1}>
                <ListItem>
                  <b>RBT12</b> = receita bruta dos 12 meses anteriores. Ordem: início de atividade
                  (regra da LC) → o da Receita (declaração do próprio mês ou rascunho) → o nosso
                  histórico. Empresa nova: no 1º mês, receita × 12; nos 11 seguintes, média × 12.
                </ListItem>
                <ListItem>
                  <b>Faixa</b> pelo RBT12 (até 180 mil, 360 mil, 720 mil, 1,8 mi, 3,6 mi, 4,8 mi).
                </ListItem>
                <ListItem>
                  <b>Alíquota efetiva</b> = (RBT12 × alíquota nominal − parcela a deduzir) ÷ RBT12.
                </ListItem>
                <ListItem>
                  <b>Fator R</b> = folha dos 12 meses (com pró-labore, FGTS e CPP) ÷ RBT12. Com 28%
                  ou mais, o serviço do Fator R vai para o Anexo III; abaixo, Anexo V.
                </ListItem>
                <ListItem>
                  <b>Repartição</b>: a efetiva é dividida entre IRPJ, CSLL, Cofins, PIS, CPP, ICMS,
                  IPI e ISS pela tabela do anexo e faixa. Tributo fora do DAS daquela receita não é
                  cobrado. ISS limitado a 5% na 5ª faixa dos Anexos III e IV.
                </ListItem>
                <ListItem>
                  Cada tributo é arredondado no centavo e o DAS é a soma — igual ao PGDAS-D.
                </ListItem>
                <ListItem>
                  No card, passe o mouse no valor de cada anexo para ver a memória de cálculo.
                </ListItem>
              </UnorderedList>
            </Secao>

            <Secao titulo="O que a auditoria confere">
              {AUDITORIAS.map(g => (
                <Box key={g.grupo} mb={3}>
                  <Text fontWeight={700} color="#0F172A" mb={1}>
                    {g.grupo}
                  </Text>
                  {g.itens.map(([nome, nivel, explica]) => (
                    <Flex key={nome} gap={2} mb={1.5} align="flex-start">
                      <Badge colorScheme={NIVEL[nivel].cor} minW="62px" textAlign="center" mt="2px">
                        {NIVEL[nivel].rotulo}
                      </Badge>
                      <Text>
                        <b>{nome}.</b> {explica}
                      </Text>
                    </Flex>
                  ))}
                </Box>
              ))}
            </Secao>

            <Secao titulo="Receita (SERPRO): o que cada botão faz">
              <UnorderedList spacing={1}>
                <ListItem>
                  <b>Rascunho no SERPRO</b>: a Receita calcula sem transmitir. Dele sai o valor dela
                  e o RBT12 que ela usou.
                </ListItem>
                <ListItem>
                  <b>Importar da Receita</b>: só consulta a declaração do <b>mês anterior</b> ao da
                  apuração (apurando 09, lê a de 08). O PDF dela traz a receita dos meses anteriores
                  e a do próprio mês: juntas fecham os 12 meses do RBT12. Se faltar algum mês, avisa
                  qual. Não transmite.
                </ListItem>
                <ListItem>
                  <b>Transmitir</b>: só a original, das aprovadas. Mês já declarado não é enviado.
                </ListItem>
                <ListItem>
                  <b>Retificar</b>: na empresa, com o CNPJ digitado. Substitui a declaração do mês.
                </ListItem>
                <ListItem>
                  <b>Gerar DAS e extrato</b>: guia e extrato das transmitidas.
                </ListItem>
                <ListItem>
                  Toda consulta ao SERPRO é cobrada. Os PDFs (declaração, recibo, DAS, extrato)
                  ficam guardados por empresa, ano e mês.
                </ListItem>
                <ListItem>
                  A empresa precisa ter dado procuração no e-CAC ao escritório (30.102.178/0001-04).
                </ListItem>
              </UnorderedList>
            </Secao>
          </Accordion>
        </DrawerBody>
      </DrawerContent>
    </Drawer>
  );
}
